import React, { useContext, useEffect, useMemo } from 'react';
import { Routes } from 'react-router';
import { shallowEqual, useSelector } from 'react-redux';
import { StoreState } from 'src/types/global-types';

import { AsyncRouterContext } from './AsyncRouter';

type SwitchProps = {
    base: string;
    children: React.ReactNode;
};

const routerLocationSelector = (s: StoreState) => s.router.location;

/**
 * Nested SPA routes under a registered AsyncLoader "base" (e.g. /blog → /blog/:id)
 * intentionally do NOT remount the root component. Matching therefore depends on
 * `<Routes>` seeing the latest location.
 *
 * React Router's HistoryRouter can lag the shared `history@5` object on some
 * same-base navigations; Redux `router.location` is updated by AsyncLoader on
 * every history event, so we drive `<Routes>` from that source instead.
 */
export const AsyncSwitch: React.FC<SwitchProps> = (props) => {
    const { registerBase } = useContext(AsyncRouterContext);
    const routerLocation = useSelector(routerLocationSelector, shallowEqual);

    useEffect(() => registerBase(props.base.slice(1)), [props.base, registerBase]);

    const location = useMemo(
        () => ({
            pathname: routerLocation?.pathname || '/',
            search: routerLocation?.search || '',
            hash: routerLocation?.hash || '',
            state: routerLocation?.state,
            key: routerLocation?.key || routerLocation?.pathname || 'default',
        }),
        [routerLocation],
    );

    return <Routes location={location}>{props.children}</Routes>;
};
